import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
    ApiErrorPayload,
    GLOBAL_API_ERROR_EVENT,
} from '../../services/apiService';
import { useTranslation } from 'react-i18next';

const GlobalApiErrorDialog: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [errorState, setErrorState] = useState<ApiErrorPayload | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const handler = (event: Event) => {
            const customEvent = event as CustomEvent<ApiErrorPayload>;
            if (!customEvent.detail) {
                return;
            }
            setErrorState(customEvent.detail);
            setOpen(true);
        };

        window.addEventListener(GLOBAL_API_ERROR_EVENT, handler);
        return () =>
            window.removeEventListener(GLOBAL_API_ERROR_EVENT, handler);
    }, []);

    const featureLabel = useMemo(() => {
        const featureKey = errorState?.details?.feature;
        if (!featureKey) {
            return t('globalApiErrorDialog.features.default');
        }
        const translationKey = `globalApiErrorDialog.features.${featureKey}`;
        if (i18n.exists(translationKey)) {
            return t(translationKey);
        }
        return t('globalApiErrorDialog.features.default');
    }, [errorState?.details?.feature, i18n, t]);

    const errorTranslationKey = useMemo(() => {
        if (!errorState?.error) {
            return null;
        }
        const key = `globalApiErrorDialog.apiErrors.${errorState.error}`;
        return i18n.exists(`${key}.description`) ? key : null;
    }, [errorState?.error, i18n]);

    const title = useMemo(() => {
        if (errorTranslationKey && i18n.exists(`${errorTranslationKey}.title`)) {
            return t(`${errorTranslationKey}.title`);
        }
        return t('globalApiErrorDialog.generic.title');
    }, [errorTranslationKey, i18n, t]);

    const description = useMemo(() => {
        if (!errorState) {
            return '';
        }
        if (errorTranslationKey) {
            return t(`${errorTranslationKey}.description`, {
                feature: featureLabel,
            });
        }
        if (errorState.message) {
            return errorState.message;
        }
        return t('globalApiErrorDialog.generic.description');
    }, [errorState, errorTranslationKey, featureLabel, t]);

    const retryMessage = useMemo(() => {
        if (!errorState?.details?.retryAtUtc) {
            return '';
        }
        const date = new Date(errorState.details.retryAtUtc);
        if (Number.isNaN(date.getTime())) {
            return '';
        }
        return t('globalApiErrorDialog.retryAt', {
            time: date.toLocaleString(i18n.language),
        });
    }, [errorState?.details?.retryAtUtc, i18n.language, t]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {description}
                        {retryMessage ? ` ${retryMessage}` : ''}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button onClick={() => setOpen(false)}>
                        {t('globalApiErrorDialog.actions.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default GlobalApiErrorDialog;
